import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'dva';
import { injectIntl } from 'react-intl';
import { Alert, Progress } from 'antd';
import {
  recognitionNoticeType,
  RECOGNITION_NOTICE_LEVEL_NONE,
  RECOGNITION_NOTICE_LEVEL_LIMIT_REACHED,
} from '../../types';
import {
  ACTION_APP_REFRESH_RECOGNITION_NOTICE,
  ACTION_APP_DISMISS_RECOGNITION_NOTICE,
} from '../../models/app';
import { getVisibleRecognitionNotice } from '../../selectors/app';
import styles from './styles.less';

// minimal delay between two re-checks triggered by the window regaining focus
export const REFRESH_ON_FOCUS_INTERVAL_MS = 60 * 1000;

const WARNING_COLOR = '#faad14';

// used_percent can go over 100 after a package downgrade
export const clampPercent = percent => Math.min(Math.max(percent || 0, 0), 100);

// monthly_limit is null when the client has no package
const hasUsage = notice => notice.monthly_limit !== null && notice.monthly_limit !== undefined
  && notice.used_percent !== null && notice.used_percent !== undefined;

export const RecognitionUsage = ({ notice, intl }) => {
  if (!hasUsage(notice)) {
    return null;
  }

  const { monthly_limit: monthlyLimit, used_percent: usedPercent } = notice;

  const isLimitReached = notice.level === RECOGNITION_NOTICE_LEVEL_LIMIT_REACHED;

  return (
    <div className={styles.usage}>
      <Progress
        className={styles.usageProgress}
        percent={clampPercent(usedPercent)}
        status={isLimitReached ? 'exception' : 'normal'}
        strokeColor={isLimitReached ? undefined : WARNING_COLOR}
        format={() => `${usedPercent}%`}
      />
      <span className={styles.usageCount}>
        {`${intl.formatNumber(notice.recognitions_count)} / ${intl.formatNumber(monthlyLimit)}`}
      </span>
    </div>
  );
};

RecognitionUsage.propTypes = {
  notice: recognitionNoticeType.isRequired,
  intl: PropTypes.shape({ formatNumber: PropTypes.func.isRequired }).isRequired,
};

/**
 * Renders the notice as it comes from the backend: `level` decides the style,
 * `message` is already localised.
 */
export const RecognitionNoticeBanner = ({ notice, intl, onClose }) => {
  if (!notice || notice.level === RECOGNITION_NOTICE_LEVEL_NONE) {
    return null;
  }

  const isLimitReached = notice.level === RECOGNITION_NOTICE_LEVEL_LIMIT_REACHED;
  const withUsage = hasUsage(notice);
  if (!notice.message && !withUsage) {
    return null;
  }

  const usage = <RecognitionUsage notice={notice} intl={intl} />;

  return (
    <div className={styles.container}>
      <Alert
        className={styles.alert}
        type={isLimitReached ? 'error' : 'warning'}
        showIcon
        message={notice.message || usage}
        description={notice.message && withUsage ? usage : undefined}
        closable={!isLimitReached}
        onClose={onClose}
      />
    </div>
  );
};

RecognitionNoticeBanner.propTypes = {
  notice: recognitionNoticeType,
  intl: PropTypes.shape({ formatNumber: PropTypes.func.isRequired }).isRequired,
  onClose: PropTypes.func,
};

RecognitionNoticeBanner.defaultProps = {
  notice: null,
  onClose: () => {},
};

export const RecognitionNoticeView = ({ notice, level, intl, dispatch }) => {
  const lastRefreshAt = useRef(Date.now());

  // re-check when the app regains focus, so the banner clears once an upgrade lifts the block
  useEffect(() => {
    if (level === RECOGNITION_NOTICE_LEVEL_NONE) {
      return undefined;
    }

    const refresh = () => {
      if (document.visibilityState === 'hidden'
        || Date.now() - lastRefreshAt.current < REFRESH_ON_FOCUS_INTERVAL_MS) {
        return;
      }
      lastRefreshAt.current = Date.now();
      dispatch({ type: ACTION_APP_REFRESH_RECOGNITION_NOTICE });
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [level]);

  return (
    <RecognitionNoticeBanner
      notice={notice}
      intl={intl}
      onClose={() => dispatch({ type: ACTION_APP_DISMISS_RECOGNITION_NOTICE })}
    />
  );
};

export default connect(({ app }) => ({
  notice: getVisibleRecognitionNotice(app),
  level: app.user.recognition_notice ? app.user.recognition_notice.level : RECOGNITION_NOTICE_LEVEL_NONE,
}))(injectIntl(RecognitionNoticeView));



// WEBPACK FOOTER //
// ./src/components/RecognitionNotice/RecognitionNotice.js